import React from "react";
import styled from "styled-components";
import { theme } from "../styles/theme";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "outline";
  error?: string;
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const StyledLabel = styled.label`
  color: ${({ theme }) => theme.colors.verae[600]};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const sizeStyles = {
  sm: `
    padding: 4px 8px;
    font-size: 0.875rem;
    border-radius: 4px;
  `,
  md: `
    padding: 8px 12px;
    font-size: 1rem;
    border-radius: 6px;
  `,
  lg: `
    padding: 16px 16px;
    font-size: 1.125rem;
    border-radius: 6px;
  `,
};

const variantStyles = {
  default: `
    background-color: ${theme.colors.background};
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.border};
  `,
  outline: `
    background-color: ${theme.colors.background};
    box-shadow: inset 0 0 0 1px ${theme.colors.verae[300]};
    color: ${theme.colors.text};
  `,
};

const StyledInput = styled.input<{
  size?: "sm" | "md" | "lg";
  variant?: "default" | "outline";
  hasError?: boolean;
}>`
  width: 100%;
  transition: border-color 0.2s;
  border: none;
  ${({ size }) => size && sizeStyles[size]}
  ${({ variant }) => variant && variantStyles[variant]}
  ${({ hasError }) => hasError && `box-shadow: inset 0 0 0 2px ${theme.colors.error};`}

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${theme.colors.verae[400]};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

export const Input: React.FC<InputProps> = ({
  label,
  size = "md",
  variant = "default",
  error,
  ...props
}) => {
  return (
    <InputContainer>
      {label && <StyledLabel>{label}</StyledLabel>}
      <StyledInput size={size} variant={variant} hasError={!!error} {...props} />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </InputContainer>
  );
};
